import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Users, Plus, Trash2, Shield, Crown, Mail } from "lucide-react";
import { pageTransition, fadeUp } from "@/lib/animations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { toast } from "sonner";

export default function BeneficiaryPage() {
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showClaimCode, setShowClaimCode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", relationship: "", is_primary: false });

  const loadBeneficiaries = async () => {
    try {
      const res = await api.get("/beneficiary");
      setBeneficiaries(res.data.beneficiaries);
    } catch {
      toast.error("Failed to load beneficiaries");
    }
  };

  useEffect(() => { loadBeneficiaries(); }, []);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/beneficiary", form);
      toast.success("Beneficiary added");
      setShowNew(false);
      setForm({ name: "", email: "", relationship: "", is_primary: false });
      setShowClaimCode(res.data);
      loadBeneficiaries();
    } catch {
      toast.error("Failed to add beneficiary");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this beneficiary?")) return;
    try {
      await api.delete(`/beneficiary/${id}`);
      toast.success("Beneficiary removed");
      loadBeneficiaries();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const handleTogglePrimary = async (ben) => {
    try {
      await api.put(`/beneficiary/${ben.beneficiary_id}`, { is_primary: !ben.is_primary });
      toast.success(ben.is_primary ? "Removed as primary" : "Set as primary beneficiary");
      loadBeneficiaries();
    } catch {
      toast.error("Failed to update");
    }
  };

  return (
    <motion.div variants={pageTransition} initial="hidden" animate="visible" className="p-6 md:p-12 max-w-6xl mx-auto" data-testid="beneficiary-page">
      <motion.div variants={fadeUp} className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-2 font-medium">Will & Access</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Beneficiaries
          </h1>
          <p className="text-sm text-slate-400 mt-1">Manage who receives access to your vault</p>
        </div>
        <Button
          onClick={() => setShowNew(true)}
          data-testid="beneficiary-add-btn"
          className="bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full px-6 font-bold shadow-[0_0_20px_rgba(0,255,136,0.3)]"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Beneficiary
        </Button>
      </motion.div>
      {/* Info Banner */}
      <div className="bg-white/[0.03] backdrop-blur-xl border border-[#00FF88]/10 rounded-[20px] p-6 mb-8 flex items-start gap-4">
        <Shield className="w-5 h-5 text-[#00FF88] mt-0.5 shrink-0" />
        <div>
          <p className="text-sm text-white font-medium mb-1">About Your Will</p>
          <p className="text-xs text-slate-400 leading-relaxed">
            Your primary beneficiary will receive admin rights to your entire vault upon verification of your passing.
            They will be able to access all stored memories and manage the delivery of your legacy messages.
          </p>
        </div>
      </div>

      {beneficiaries.length === 0 ? (
        <div className="text-center py-24">
          <Users className="w-12 h-12 text-slate-700 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">No beneficiaries yet</p>
          <p className="text-slate-600 text-sm mt-2">Add someone you trust to manage your legacy</p>
        </div>
      ) : (
        <div className="space-y-4">
          {beneficiaries.map((ben, i) => (
            <motion.div
              key={ben.beneficiary_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className={`bg-white/[0.03] backdrop-blur-xl border rounded-[20px] p-6 group ${
                ben.is_primary ? "border-[#00FF88]/20" : "border-white/[0.06]"
              } hover:border-white/15 transition-colors duration-300`}
              data-testid={`beneficiary-${ben.beneficiary_id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    ben.is_primary ? "bg-[#00FF88]/10" : "bg-white/5"
                  }`}>
                    {ben.is_primary ? (
                      <Crown className="w-5 h-5 text-[#00FF88]" />
                    ) : (
                      <Users className="w-5 h-5 text-slate-500" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-medium text-white" style={{ fontFamily: 'Manrope' }}>{ben.name}</h3>
                      {ben.is_primary && (
                        <span className="text-xs bg-[#00FF88]/10 text-[#00FF88] px-2 py-0.5 rounded-full font-medium">
                          Primary
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-sm text-slate-400 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {ben.email}
                      </span>
                      {ben.relationship && (
                        <span className="text-xs text-slate-500">{ben.relationship}</span>
                      )}
                    </div>
                    {ben.claim_code && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-slate-600">Claim Code:</span>
                        <code className="text-xs bg-white/5 text-[#00FF88] px-2 py-0.5 rounded font-mono" data-testid={`claim-code-${ben.beneficiary_id}`}>
                          {ben.claim_code}
                        </code>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Primary</span>
                    <Switch
                      checked={ben.is_primary}
                      onCheckedChange={() => handleTogglePrimary(ben)}
                      data-testid={`primary-toggle-${ben.beneficiary_id}`}
                    />
                  </div>
                  <button
                    onClick={() => handleDelete(ben.beneficiary_id)}
                    data-testid={`beneficiary-delete-${ben.beneficiary_id}`}
                    className="text-slate-500 hover:text-red-400 transition-colors duration-200"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[#111]/95 backdrop-blur-2xl border-white/[0.1] text-white max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Add Beneficiary</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="beneficiary-name-input"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12"
            />
            <Input
              placeholder="Email address"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="beneficiary-email-input"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12"
            />
            <Input
              placeholder="Relationship (e.g., Spouse, Child, Sibling)"
              value={form.relationship}
              onChange={(e) => setForm({ ...form, relationship: e.target.value })}
              data-testid="beneficiary-relationship-input"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12"
            />
            <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/[0.06]">
              <div>
                <p className="text-sm text-white font-medium">Primary Beneficiary</p>
                <p className="text-xs text-slate-500 mt-0.5">Will receive admin rights to your vault</p>
              </div>
              <Switch
                checked={form.is_primary}
                onCheckedChange={(checked) => setForm({ ...form, is_primary: checked })}
                data-testid="beneficiary-primary-switch"
              />
            </div>
            <Button
              onClick={handleCreate}
              disabled={saving}
              data-testid="beneficiary-save-btn"
              className="w-full bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full h-12 font-bold"
            >
              {saving ? "Adding..." : "Add Beneficiary"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Claim Code Success Dialog */}
      <Dialog open={!!showClaimCode} onOpenChange={() => setShowClaimCode(null)}>
        <DialogContent className="bg-[#111]/95 backdrop-blur-2xl border-[#00FF88]/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Beneficiary Added</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <p className="text-sm text-slate-400">
              <strong className="text-white">{showClaimCode?.name}</strong> has been added as your beneficiary. Share the claim code below with them securely.
            </p>
            <div className="bg-white/5 border border-[#00FF88]/20 rounded-xl p-6 text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Claim Code</p>
              <code className="text-2xl font-bold text-[#00FF88] font-mono tracking-widest" data-testid="new-claim-code">
                {showClaimCode?.claim_code}
              </code>
            </div>
            <div className="bg-white/5 rounded-xl p-4">
              <p className="text-xs text-slate-400 leading-relaxed">
                This code allows your beneficiary to claim access to your vault when the time comes. 
                An email notification has been queued to <strong className="text-white">{showClaimCode?.email}</strong>.
              </p>
            </div>
            <Button
              onClick={() => {
                navigator.clipboard?.writeText(showClaimCode?.claim_code || "");
                toast.success("Claim code copied!");
              }}
              data-testid="copy-claim-code-btn"
              className="w-full bg-white text-black hover:bg-slate-200 rounded-full h-12 font-medium"
            >
              Copy Code & Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
